import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Skeleton } from 'skullet/react'
import { create } from '@bufbuild/protobuf'

import { QuoteLineItemSchema, QuoteSchema, type Quote, type QuoteLite } from '@/gen/cortado/v1/invoices_pb'
import { useQuotesByIds } from '@/stores/quotes.store'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  quoteCompareAmount,
  quoteCompareDifference,
  quoteCompareLines,
  quoteCompareQuantity,
  quoteCompareTotals,
  type QuoteCompareSide,
} from '@/components/pipeline/quote-compare-view'

function sampleQuote(quoteNumber: number, title: string, lines: [string, bigint][]): Quote {
  const subtotalCents = lines.reduce((sum, [, cents]) => sum + cents, 0n)
  return create(QuoteSchema, {
    id: `sample-${quoteNumber}`,
    quoteNumber,
    title,
    lineItems: lines.map(([name, lineTotalCents], sortOrder) =>
      create(QuoteLineItemSchema, { name, lineTotalCents, quantity: 1, unit: 'Per Event', sortOrder }),
    ),
    subtotalCents,
    totalCents: subtotalCents,
    totalWithTipCents: subtotalCents,
  })
}

const SAMPLE_PAIR = {
  left: sampleQuote(1042, 'Full service dinner', [
    ['Plated dinner', 360000n],
    ['Bar service', 90000n],
    ['Dessert table', 35000n],
  ]),
  right: sampleQuote(1043, 'Buffet option', [
    ['Buffet dinner', 240000n],
    ['Bar service', 90000n],
  ]),
}

interface QuoteCompareSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  jobId: string
  /** The quote open on the page; always the left column. */
  quoteId: string
  quoteNumber: number
  /** The job's other quotes in switcher order; the first is compared until another is picked. */
  others: QuoteLite[]
  acceptedQuoteId?: string
}

/**
 * The open quote next to another quote on the same job: each line item's
 * quantity and total on both, then the totals, with what the other quote adds
 * or saves. Read only; it compares the saved quotes.
 */
export function QuoteCompareSheet({
  open,
  onOpenChange,
  jobId,
  quoteId,
  quoteNumber,
  others,
  acceptedQuoteId,
}: QuoteCompareSheetProps) {
  const [pickedId, setPickedId] = useState<string | undefined>(undefined)
  const otherId = others.find((lite) => lite.id === pickedId)?.id ?? others[0]?.id
  const other = others.find((lite) => lite.id === otherId)

  const byId = useQuotesByIds(open && otherId ? [quoteId, otherId] : [])
  const left = byId[quoteId]?.quote
  const right = otherId ? byId[otherId]?.quote : undefined
  const isLoading = Boolean(byId[quoteId]?.isLoading || (otherId && byId[otherId]?.isLoading))
  const isError = Boolean(byId[quoteId]?.isError || (otherId && byId[otherId]?.isError))

  // The content and footer classes are PIPELINE_SHEET_CONTENT_CLASS and
  // PIPELINE_SHEET_FOOTER_CLASS (shared/sheet-layout.ts), inlined so the design
  // lint can check them. Keep them in step.
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Compare quotes</SheetTitle>
          <SheetDescription>Saved line items and totals. Differences are the other quote against #{quoteNumber}.</SheetDescription>
        </SheetHeader>
        <SheetBody>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <div className="text-xs text-muted-foreground">Compare #{quoteNumber} with</div>
              <div role="group" aria-label="Quote to compare with" className="flex flex-wrap gap-1.5">
                {others.map((lite) => (
                  <Button
                    key={lite.id}
                    type="button"
                    size="sm"
                    variant="outline"
                    aria-pressed={lite.id === otherId}
                    className={cn('h-auto py-1', lite.id === otherId && 'border-brand bg-card')}
                    onClick={() => setPickedId(lite.id)}
                  >
                    <span className="font-medium">#{lite.quoteNumber}</span>
                    {lite.id === acceptedQuoteId && <span className="text-xs text-muted-foreground">Accepted</span>}
                    {lite.title && <span className="max-w-40 truncate text-xs text-muted-foreground">{lite.title}</span>}
                  </Button>
                ))}
              </div>
            </div>

            {isError ? (
              <p className="text-sm text-destructive">We couldn't load these quotes. Close and try again.</p>
            ) : (
              <Skeleton
                data={isLoading || !left || !right ? null : { left, right }}
                sample={SAMPLE_PAIR}
                label="Loading quotes"
              >
                {({ left, right }) => <CompareTable left={left} right={right} />}
              </Skeleton>
            )}
          </div>
        </SheetBody>
        <SheetFooter className="sm:justify-between">
          <SheetClose asChild>
            <Button type="button" variant="outline">
              Close
            </Button>
          </SheetClose>
          {other && (
            <Button asChild>
              <Link
                to="/pipeline/$id/quotes/$quoteId"
                params={{ id: jobId, quoteId: other.id }}
                onClick={() => onOpenChange(false)}
              >
                Open #{other.quoteNumber}
              </Link>
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

function SideCell({ side }: { side?: QuoteCompareSide }) {
  if (!side) {
    return <td className="px-2 py-2 text-right text-muted-foreground">Not included</td>
  }
  return (
    <td className="px-2 py-2 text-right">
      <div className="font-financial">{quoteCompareAmount(side.totalCents)}</div>
      <div className="text-xs text-muted-foreground">{quoteCompareQuantity(side)}</div>
    </td>
  )
}

function DifferenceCell({ leftCents, rightCents }: { leftCents: number; rightCents: number }) {
  const diff = rightCents - leftCents
  return (
    <td
      className={cn(
        'font-financial py-2 pl-2 text-right text-xs',
        diff === 0 ? 'text-muted-foreground' : 'font-medium text-foreground',
      )}
    >
      {quoteCompareDifference(leftCents, rightCents)}
    </td>
  )
}

function CompareTable({ left, right }: { left: Quote; right: Quote }) {
  const lines = quoteCompareLines(left, right)
  const totals = quoteCompareTotals(left, right)
  return (
    <table className="w-full table-fixed text-sm">
      <colgroup>
        <col />
        <col className="w-28" />
        <col className="w-28" />
        <col className="w-20" />
      </colgroup>
      <thead>
        <tr className="border-b text-xs text-muted-foreground">
          <th scope="col" className="py-2 pr-2 text-left font-medium">
            Item
          </th>
          <th scope="col" className="px-2 py-2 text-right font-medium">
            <QuoteHeading quote={left} />
          </th>
          <th scope="col" className="px-2 py-2 text-right font-medium">
            <QuoteHeading quote={right} />
          </th>
          <th scope="col" className="py-2 pl-2 text-right font-medium">
            Difference
          </th>
        </tr>
      </thead>
      <tbody>
        {lines.length === 0 && (
          <tr>
            <td colSpan={4} className="py-4 text-center text-muted-foreground">
              Neither quote has line items yet.
            </td>
          </tr>
        )}
        {lines.map((line) => (
          <tr key={line.key} className="border-b align-top">
            <th scope="row" className="py-2 pr-2 text-left font-normal break-words">
              {line.name}
            </th>
            <SideCell side={line.left} />
            <SideCell side={line.right} />
            <DifferenceCell leftCents={line.left?.totalCents ?? 0} rightCents={line.right?.totalCents ?? 0} />
          </tr>
        ))}
      </tbody>
      <tfoot>
        {totals.map((row) => {
          const isTotal = row.key === 'total' || row.key === 'totalWithTip'
          return (
            <tr key={row.key} className={cn(isTotal && 'border-t font-semibold')}>
              <th scope="row" className={cn('py-1.5 pr-2 text-left', !isTotal && 'font-normal text-muted-foreground')}>
                {row.label}
              </th>
              <td className="font-financial px-2 py-1.5 text-right">{quoteCompareAmount(row.leftCents)}</td>
              <td className="font-financial px-2 py-1.5 text-right">{quoteCompareAmount(row.rightCents)}</td>
              <DifferenceCell leftCents={row.leftCents} rightCents={row.rightCents} />
            </tr>
          )
        })}
      </tfoot>
    </table>
  )
}

function QuoteHeading({ quote }: { quote: Quote }) {
  return (
    <>
      <span className="block text-foreground">#{quote.quoteNumber}</span>
      {quote.title && <span className="block truncate font-normal">{quote.title}</span>}
    </>
  )
}
